import React, {useEffect, useState} from 'react';
import {CustomCard} from "../../../../../components/UI/AntCustom/CustomCard";
import {CustomButton} from "../../../../../components/UI/AntCustom/CustomButton";
import {Table, Popconfirm, Tag, message} from "antd";
import {useIntl, defineMessages} from "react-intl";
import {useAppDispatch} from "../../../../../store/store";
import {BillingApi} from "../../../../../API/ApiClasses/BillingApi";
import {
  BillingApiGetPaymentMethodsRequest,
  BillingApiDetachPaymentMethodRequest,
  BillingApiUpdateDefaultPaymentMethodRequest,
  BillingApiPaymentMethodEntity,
} from "../../../../../proto/generated/api_entities_pb";
import {Loader} from "../../../../../components/UI/Loader/Loader";
import {PaymentMethodModal} from "./PaymentMethodModal";
import {getTariff} from "../../../../../store/reducers/tariffReducer/tariffReducer";

const PaymentMethodsListMessages = defineMessages({
  title: {
    id: "paymentMethodsList.title",
    defaultMessage: "Payment Methods",
  },
  cardNumber: {
    id: "paymentMethodsList.cardNumber",
    defaultMessage: "Card Number",
  },
  brand: {
    id: "paymentMethodsList.brand",
    defaultMessage: "Brand",
  },
  expiry: {
    id: "paymentMethodsList.expiry",
    defaultMessage: "Expires",
  },
  default: {
    id: "paymentMethodsList.default",
    defaultMessage: "Default",
  },
  setAsDefault: {
    id: "paymentMethodsList.setAsDefault",
    defaultMessage: "Set as default",
  },
  delete: {
    id: "paymentMethodsList.delete",
    defaultMessage: "Delete",
  },
  deleteConfirm: {
    id: "paymentMethodsList.deleteConfirm",
    defaultMessage: "Are you sure you want to delete this payment method?",
  },
  addCard: {
    id: "paymentMethodsList.addCard",
    defaultMessage: "Add Card",
  },
  noPaymentMethods: {
    id: "paymentMethodsList.noPaymentMethods",
    defaultMessage: "No payment methods found. Add a card to get started.",
  },
  loading: {
    id: "paymentMethodsList.loading",
    defaultMessage: "Loading...",
  },
});

const formatCardNumber = (last4: string): string => {
  return `•••• •••• •••• ${last4}`;
};

const formatBrand = (brand: string): string => {
  return brand.charAt(0).toUpperCase() + brand.slice(1);
};

const formatExpiry = (month: number, year: number): string => {
  return `${String(month).padStart(2, '0')}/${year}`;
};

export const PaymentMethodsList: React.FC = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [paymentMethods, setPaymentMethods] = useState<BillingApiPaymentMethodEntity[]>([]);
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [setDefaultLoading, setSetDefaultLoading] = useState<string | null>(null);
  const [isPaymentMethodModalVisible, setIsPaymentMethodModalVisible] = useState(false);

  const fetchPaymentMethods = async () => {
    try {
      setLoading(true);
      const req = new BillingApiGetPaymentMethodsRequest();
      const res = await BillingApi.getPaymentMethods(req);
      
      if (res.getResultresponse()?.getIssuccessful()) {
        setPaymentMethods(res.getPaymentmethodsList());
        setDefaultPaymentMethodId(res.getDefaultpaymentmethodid() || null);
      }
    } catch (error) {
      console.error('Failed to fetch payment methods:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const handleDelete = async (paymentMethodId: string) => {
    try {
      const req = new BillingApiDetachPaymentMethodRequest();
      req.setPaymentmethodid(paymentMethodId);
      const res = await BillingApi.detachPaymentMethod(req);
      if (res.getResultresponse()?.getIssuccessful()) {
        await fetchPaymentMethods();
      } else {
        const errMsg = res.getResultresponse()?.getErrormessage() || 'Failed to delete payment method';
        message.error(errMsg);
      }
    } catch (error: any) {
      const errMsg = error?.message || error?.response?.data?.errorMessage || 'Failed to delete payment method';
      message.error(errMsg);
      console.error('Failed to delete payment method:', error);
    }
  };

  const handlePaymentMethodSuccess = () => {
    fetchPaymentMethods();
    setIsPaymentMethodModalVisible(false);
    dispatch(getTariff({silent: true}));
  };

  const handleSetAsDefault = async (paymentMethodId: string) => {
    try {
      setSetDefaultLoading(paymentMethodId);
      const req = new BillingApiUpdateDefaultPaymentMethodRequest();
      req.setPaymentmethodid(paymentMethodId);
      const res = await BillingApi.updateDefaultPaymentMethod(req);
      if (res.getResultresponse()?.getIssuccessful()) {
        setDefaultPaymentMethodId(paymentMethodId);
        dispatch(getTariff({silent: true}));
      }
    } catch (error) {
      console.error('Failed to set default payment method:', error);
    } finally {
      setSetDefaultLoading(null);
    }
  };

  const columns = [
    {
      title: intl.formatMessage(PaymentMethodsListMessages.cardNumber),
      key: 'cardNumber',
      render: (_: any, record: BillingApiPaymentMethodEntity) => (
        <span style={{display: 'flex', alignItems: 'center', gap: 8}}>
          {formatCardNumber(record.getCardlast4())}
          {defaultPaymentMethodId === record.getPaymentmethodid() && (
            <Tag color="blue">{intl.formatMessage(PaymentMethodsListMessages.default)}</Tag>
          )}
        </span>
      ),
    },
    {
      title: intl.formatMessage(PaymentMethodsListMessages.brand),
      key: 'brand',
      render: (_: any, record: BillingApiPaymentMethodEntity) => formatBrand(record.getCardbrand()),
    },
    {
      title: intl.formatMessage(PaymentMethodsListMessages.expiry),
      key: 'expiry',
      render: (_: any, record: BillingApiPaymentMethodEntity) => 
        formatExpiry(record.getCardexpmonth(), record.getCardexpyear()),
    },
    {
      title: '',
      key: 'actions',
      render: (_: any, record: BillingApiPaymentMethodEntity) => {
        const pmId = record.getPaymentmethodid();
        const isDefault = defaultPaymentMethodId === pmId;
        return (
          <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
            {!isDefault && (
              <CustomButton
                size="small"
                type="primary"
                ghost
                loading={setDefaultLoading === pmId}
                onClick={() => handleSetAsDefault(pmId)}
              >
                {intl.formatMessage(PaymentMethodsListMessages.setAsDefault)}
              </CustomButton>
            )}
            <Popconfirm
              title={intl.formatMessage(PaymentMethodsListMessages.deleteConfirm)}
              onConfirm={() => handleDelete(pmId)}
              okText="Yes"
              cancelText="No"
            >
              <CustomButton danger size="small">
                {intl.formatMessage(PaymentMethodsListMessages.delete)}
              </CustomButton>
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  if (loading) {
    return (
      <CustomCard width={"max"} style={{ width: '100%' }}>
        <Loader />
      </CustomCard>
    );
  }

  return (
    <>
      <CustomCard width={"max"} style={{ width: '100%' }}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px'}}>
          <div style={{fontSize: '16px', fontWeight: 600}}>
            {intl.formatMessage(PaymentMethodsListMessages.title)}
          </div>
          <CustomButton
            type="primary"
            onClick={() => setIsPaymentMethodModalVisible(true)}
          >
            {intl.formatMessage(PaymentMethodsListMessages.addCard)}
          </CustomButton>
        </div>
        {paymentMethods.length === 0 ? (
          <div style={{textAlign: 'center', padding: '24px', color: '#999999'}}>
            {intl.formatMessage(PaymentMethodsListMessages.noPaymentMethods)}
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={paymentMethods}
            rowKey={(record) => record.getPaymentmethodid() || `pm-${paymentMethods.indexOf(record)}`}
            pagination={false}
          />
        )}
      </CustomCard>
      
      <PaymentMethodModal
        isVisible={isPaymentMethodModalVisible}
        setIsVisible={setIsPaymentMethodModalVisible}
        onSuccess={handlePaymentMethodSuccess}
      />
    </>
  );
};
